import React, { ComponentProps } from "react";
import PercentageChangeText from "@/lib/molecules/percentageChangeText";
import { Card, CardContent } from "@/lib/shadcn/ui/card";
import { cn } from "@/lib/shadcn/utils";
import { CalendarIcon } from "@radix-ui/react-icons";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/lib/shadcn/ui/tooltip";
import { Info } from "lucide-react";

export type StatsCardProps = ComponentProps<"div"> & {
    title: string;
    value: number;
    lasPeriodValue: number;
    lastYearValue: number;
    periodDescription: string;
    valueFormatter?: (value: number) => string;
    CardIcon?: React.FC<{ className?: string }>;
    iconVariant?: "primary" | "volt" | "green" | "blue" | "orange" | "red";
    iconBgClassName?: string;
    subtitle?: string;
    dense?: boolean;
    tooltip?: string;
};

// No-baseline marker: shown when the previous-period/year value is 0 so there is
// no meaningful percentage change to compute (avoids "Infinity%"/"NaN%").
const NO_BASELINE = "—";

// Returns the percentage change, or null when it is not finite (prev === 0 → the
// division is Infinity/NaN). Callers render the no-baseline marker for null.
const safePercentageChange = (value: number, previous: number): number | null => {
    const change = ((value - previous) / previous) * 100;
    return Number.isFinite(change) ? change : null;
};

const StatsCard: React.FC<StatsCardProps> = ({
    title,
    value,
    lasPeriodValue,
    lastYearValue,
    periodDescription,
    valueFormatter = (value) => value.toFixed(0),
    CardIcon = CalendarIcon,
    iconVariant = "primary",
    className,
    iconBgClassName,
    subtitle,
    dense = false,
    tooltip,
    ...rest
}) => {
    const changeVsLastPeriod = safePercentageChange(value, lasPeriodValue);
    const changeVsLastYear = safePercentageChange(value, lastYearValue);

    const getIconBackground = () => {
        // dense tightens the chip padding so two stacked cards fit a narrow column.
        const baseClasses = cn(dense ? "p-2" : "p-3", "rounded-lg");
        const variants = {
            primary: "bg-primary text-primary-foreground",
            volt: "bg-volt text-volt-foreground",
            green: "bg-green-50",
            blue: "bg-blue-50",
            orange: "bg-orange-50",
            red: "bg-red-50",
        };

        return cn(baseClasses, variants[iconVariant], iconBgClassName);
    };

    const getIconColor = () => {
        const variants = {
            primary: "text-primary-foreground",
            volt: "text-volt-foreground",
            green: "text-green-500",
            blue: "text-blue-500",
            orange: "text-orange-500",
            // red-600, NOT red-500 — red-500 on white is ~3.9:1 and fails WCAG AA (4.5:1).
            red: "text-red-600",
        };

        return variants[iconVariant];
    };

    return (
        <Card className={cn("overflow-hidden", className)} {...rest}>
            <CardContent className={"p-3 pb-2 pt-0 ps-4"}>
                {/* Main grid layout with two columns */}
                <div className="grid grid-cols-[auto_1fr] p-2 pt-0 pb-0">
                    {" "}
                    {/* Negative margin to counteract CardContent padding */}
                    {/* Icon column */}
                    <div className="flex justify-center p-2 items-center">
                        <div className={cn(getIconBackground())}>
                            <CardIcon className={cn(dense ? "h-8 w-8" : "h-12 w-12", getIconColor())} aria-hidden="true" />
                        </div>
                    </div>
                    {/* Content column */}
                    <div className="p-2 ml-7">
                        {/* Title and value section */}
                        <div className="mb-2">
                            <div className="flex items-center gap-1">
                                <h2 className="text-1xl mb-1">{title}</h2>
                                {tooltip && (
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <button
                                                type="button"
                                                aria-label={`What is ${title}?`}
                                                data-testid="metric-info"
                                                className="text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm inline-flex"
                                            >
                                                <Info className="h-3.5 w-3.5" aria-hidden="true" />
                                            </button>
                                        </TooltipTrigger>
                                        <TooltipContent className="max-w-xs">{tooltip}</TooltipContent>
                                    </Tooltip>
                                )}
                            </div>
                            <div className="flex items-baseline space-x-2">
                                <h3 className="text-2xl font-bold">{valueFormatter(value)}</h3>
                                {subtitle && <span className="text-sm text-muted-foreground">{subtitle}</span>}
                            </div>
                        </div>

                        {/* Period and comparisons section */}
                        <div className="space-y-3">
                            {/* Period */}
                            <div className="flex items-center text-sm">
                                <CalendarIcon className="h-4 w-4 text-muted-foreground mr-1" />
                                <span className="text-muted-foreground">{periodDescription}</span>
                            </div>

                            {/* Comparisons */}
                            <div className="space-y-1">
                                {/* Previous period comparison */}
                                <div className="flex items-center text-sm">
                                    {changeVsLastPeriod === null ? (
                                        <span className="text-xs text-gray-700">{NO_BASELINE}</span>
                                    ) : (
                                        <PercentageChangeText value={changeVsLastPeriod} valueFormatter={(v) => v.toFixed(0)} />
                                    )}
                                    <span className="ml-2 text-muted-foreground">
                                        vs previous period's {valueFormatter(lasPeriodValue)}
                                    </span>
                                </div>

                                {/* Previous year comparison */}
                                <div className="flex items-center text-sm">
                                    {changeVsLastYear === null ? (
                                        <span className="text-xs text-gray-700">{NO_BASELINE}</span>
                                    ) : (
                                        <PercentageChangeText value={changeVsLastYear} valueFormatter={(v) => v.toFixed(0)} />
                                    )}
                                    <span className="ml-2 text-muted-foreground">vs previous year's {valueFormatter(lastYearValue)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

export default StatsCard;
